import { createFileRoute } from '@tanstack/react-router'
import { ProductList } from '@/components/features/products/ProductList'
import { Button } from '@/components/ui/Button'
import { Plus, Library, Search } from 'lucide-react'
import { useState, useMemo, useEffect } from 'react'
import type { Product } from '@pranala/shared'

const ITEMS_PER_PAGE = 10

// API base URL
const API_BASE_URL = typeof window !== 'undefined' ? '' : ''

// Fetch products from API
async function fetchProducts(): Promise<Product[]> {
  const response = await fetch(`${API_BASE_URL}/api/products`)
  if (!response.ok) {
    throw new Error(`Failed to fetch products: ${response.statusText}`)
  }
  const data = await response.json()
  return data.products || []
}

// Delete product via API
async function deleteProductApi(id: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/products/${id}`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    throw new Error(`Failed to delete product: ${response.statusText}`)
  }
}

export const Route = createFileRoute('/admin/products/')({
  component: AdminProducts,
})

function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [imageFilter, setImageFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  // Fetch products on mount
  useEffect(() => {
    let isMounted = true

    const loadProducts = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await fetchProducts()
        if (isMounted) {
          setProducts(data)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load products')
          console.error('Failed to load products:', err)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadProducts()

    return () => {
      isMounted = false
    }
  }, [])

  // Handle delete product
  const handleDeleteProduct = async (id: number) => {
    if (!confirm('Apakah Anda yakin ingin menghapus produk ini?')) {
      return
    }

    try {
      await deleteProductApi(id)
      // Remove product from local state
      setProducts((prev) => prev.filter((p) => p.id !== id))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete product')
      console.error('Failed to delete product:', err)
    }
  }

  // Filter products based on search and filters
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesSearch =
          product.title.toLowerCase().includes(query) ||
          (product.author?.toLowerCase().includes(query) ?? false) ||
          (product.sku?.toLowerCase().includes(query) ?? false)
        if (!matchesSearch) return false
      }

      // Status filter
      if (statusFilter && product.status !== statusFilter) {
        return false
      }

      // Image filter
      if (imageFilter) {
        const hasImage = !!product.mediaCover
        if (imageFilter === 'has-image' && !hasImage) return false
        if (imageFilter === 'no-image' && hasImage) return false
      }

      return true
    })
  }, [products, searchQuery, statusFilter, imageFilter])

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredProducts, currentPage])

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handleStatusChange = (value: string) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handleImageFilterChange = (value: string) => {
    setImageFilter(value)
    setCurrentPage(1)
  }

  // Build route path for product edit
  const getEditRoute = (id: number) => `/admin/products/${id}/edit`

  return (
    <div className="space-y-6 mx-4 my-10">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-brand-slate-200">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Library className="w-6 h-6 text-brand-yellow-500" strokeWidth={1.5} />
            <h1 className="text-2xl lg:text-3xl text-brand-slate-800 tracking-tight">
              Produk
            </h1>
          </div>
          <p className="text-brand-slate-500">Kelola data dan gambar produk buku Anda</p>
        </div>
        <Button
          asChild
          className="bg-brand-yellow-400 hover:bg-brand-yellow-500 text-brand-slate-900 font-medium rounded-lg border-0 shadow-sm hover:shadow-md transition-all"
        >
          <a href="/admin/products/create">
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Tambah Produk
          </a>
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">Error loading products</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-sm font-medium text-red-700 hover:text-red-800 underline"
          >
            Coba Lagi
          </button>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-brand-slate-200 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-slate-400" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Cari produk..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            disabled={loading}
            className="w-full pl-10 pr-4 py-2 bg-brand-slate-50 border border-brand-slate-200 rounded-lg text-sm text-brand-slate-700 placeholder:text-brand-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-yellow-400/20 focus:border-brand-yellow-400 transition-all disabled:opacity-50"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={loading}
            className="px-4 py-2 bg-brand-slate-50 border border-brand-slate-200 rounded-lg text-sm text-brand-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-yellow-400/20 focus:border-brand-yellow-400 disabled:opacity-50"
          >
            <option value="">Semua Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={imageFilter}
            onChange={(e) => handleImageFilterChange(e.target.value)}
            disabled={loading}
            className="px-4 py-2 bg-brand-slate-50 border border-brand-slate-200 rounded-lg text-sm text-brand-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-yellow-400/20 focus:border-brand-yellow-400 disabled:opacity-50"
          >
            <option value="">Semua Kategori</option>
            <option value="has-image">Ada Gambar</option>
            <option value="no-image">Belum Ada Gambar</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl shadow-sm border border-brand-slate-200 p-12">
          <div className="flex flex-col items-center justify-center text-brand-slate-400">
            <div className="w-12 h-12 border-4 border-brand-slate-200 border-t-brand-yellow-400 rounded-full animate-spin mb-4"></div>
            <p className="text-lg font-medium text-brand-slate-600">Memuat produk...</p>
          </div>
        </div>
      )}

      {/* Products Table */}
      {!loading && (
        <ProductList
          products={paginatedProducts}
          totalProducts={filteredProducts.length}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onDelete={handleDeleteProduct}
          getEditRoute={getEditRoute}
        />
      )}
    </div>
  )
}
